import React from "react";
import { Check, X as XIcon, ShieldCheck, Info } from "@phosphor-icons/react";

/**
 * Read-only role → capability matrix.
 *
 * Data-driven (not fetched) — mirrors the backend `require_role(...)` gates
 * enforced across `routes/*.py`. When those change, update `MATRIX` below.
 */

const ROLES = [
    { id: "superadmin",     label: "Superadmin",     sub: "Platform owner" },
    { id: "admin",          label: "Admin",          sub: "Workspace owner" },
    { id: "hiring_manager", label: "Hiring Manager", sub: "Hiring lead" },
    { id: "recruiter",      label: "Recruiter",      sub: "Sourcer / screener" },
    { id: "candidate",      label: "Candidate",      sub: "Job seeker portal" },
];

// Capability rows. Each `perms` map keyed by role id.
const MATRIX = [
    {
        section: "Team",
        rows: [
            { label: "Invite / edit / delete team members",     perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 0, candidate: 0 } },
            { label: "Promote a user to Admin",                  perms: { superadmin: 1, admin: 1, hiring_manager: 0, recruiter: 0, candidate: 0 } },
            { label: "Edit / delete Admin accounts",             perms: { superadmin: 1, admin: 1, hiring_manager: 0, recruiter: 0, candidate: 0 } },
        ],
    },
    {
        section: "Requirements / Jobs",
        rows: [
            { label: "View requirements",                         perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
            { label: "Create / edit / delete requirement",        perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 0, candidate: 0 } },
            { label: "Share public link · import CSV",            perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
        ],
    },
    {
        section: "Candidates",
        rows: [
            { label: "Add / edit candidate",                       perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
            { label: "Move stage forward",                         perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
            { label: "Move stage backward from Offer/Hired",       perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 0, candidate: 0 } },
            { label: "Screening AI · notes · resume actions",      perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
            { label: "Re-parse / download / share resume PDF",     perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
            { label: "Delete candidate",                            perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
        ],
    },
    {
        section: "Interviews",
        rows: [
            { label: "Schedule / reschedule interview",            perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
            { label: "Connect Google Calendar",                    perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 0 } },
            { label: "View own interview list",                    perms: { superadmin: 1, admin: 1, hiring_manager: 1, recruiter: 1, candidate: 1 } },
        ],
    },
    {
        section: "Workspace / Billing",
        rows: [
            { label: "Configure workspace (name, branding)",       perms: { superadmin: 1, admin: 1, hiring_manager: 0, recruiter: 0, candidate: 0 } },
            { label: "Manage subscription / view invoices",        perms: { superadmin: 1, admin: 1, hiring_manager: 0, recruiter: 0, candidate: 0 } },
            { label: "Rotate integration API keys (Resend etc.)",  perms: { superadmin: 1, admin: 1, hiring_manager: 0, recruiter: 0, candidate: 0 } },
        ],
    },
    {
        section: "Platform (Superadmin only)",
        rows: [
            { label: "Provision / suspend workspaces",              perms: { superadmin: 1, admin: 0, hiring_manager: 0, recruiter: 0, candidate: 0 } },
            { label: "Impersonate any user",                        perms: { superadmin: 1, admin: 0, hiring_manager: 0, recruiter: 0, candidate: 0 } },
            { label: "Cross-workspace analytics",                   perms: { superadmin: 1, admin: 0, hiring_manager: 0, recruiter: 0, candidate: 0 } },
        ],
    },
];


export default function RolePermissionsMatrix() {
    return (
        <div className="p-4 sm:p-8 max-w-[1400px] mx-auto" data-testid="role-permissions-matrix">
            <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                    Settings
                </p>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
                    <ShieldCheck size={28} weight="bold" className="text-cobalt" />
                    Role permissions
                </h1>
                <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                    Reference for what each role can and cannot do in NaukriDaddy.
                    Changes here mirror the backend — if a capability shifts, this
                    matrix is updated alongside.
                </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 mb-6 flex items-start gap-2" data-testid="rpm-hint">
                <Info size={16} weight="bold" className="text-blue-700 shrink-0 mt-0.5" />
                <p>
                    <b>Read-only.</b> This page shows the current permission model. To adjust
                    what a specific user can do, change their role from{" "}
                    <a href="/users" className="underline font-bold">Team →</a>.
                </p>
            </div>

            {/* Mobile: per-capability role tiles */}
            <div className="sm:hidden space-y-5" data-testid="rpm-mobile">
                {MATRIX.map((section) => (
                    <div key={`m-sec-${section.section}`}>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-2 px-1">
                            {section.section}
                        </p>
                        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                            {section.rows.map((row) => (
                                <div key={`m-${row.label}`} className="px-4 py-3">
                                    <p className="text-sm text-ink mb-2">{row.label}</p>
                                    <div className="grid grid-cols-5 gap-1">
                                        {ROLES.map((r) => (
                                            <div key={r.id}
                                                 data-testid={`rpm-cell-mobile-${r.id}-${row.label.replace(/\s+/g, "-").slice(0, 30)}`}
                                                 className="flex flex-col items-center gap-1 border border-[#F5F5F5] py-1.5">
                                                <span className="text-[9px] font-bold text-[#525252] leading-tight text-center">
                                                    {r.label.split(" ")[0]}
                                                </span>
                                                {row.perms[r.id] ? (
                                                    <Check size={14} weight="bold" className="text-emerald-600" />
                                                ) : (
                                                    <XIcon size={12} weight="bold" className="text-[#D4D4D4]" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop matrix */}
            <div className="hidden sm:block border border-[#E5E5E5] overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <th className="text-left px-4 py-3 font-bold uppercase text-[10px] tracking-[0.2em] text-[#525252] sticky left-0 bg-[#FAFAFA] min-w-[280px]">
                                Capability
                            </th>
                            {ROLES.map((r) => (
                                <th
                                    key={r.id}
                                    className="px-3 py-3 text-center min-w-[110px]"
                                    data-testid={`rpm-col-${r.id}`}
                                >
                                    <p className="font-bold text-xs text-ink">{r.label}</p>
                                    <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-normal">
                                        {r.sub}
                                    </p>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {MATRIX.map((section) => (
                            <React.Fragment key={`sec-${section.section}`}>
                                <tr>
                                    <td
                                        colSpan={ROLES.length + 1}
                                        className="bg-[#F5F5F5] px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] border-b border-[#E5E5E5]"
                                    >
                                        {section.section}
                                    </td>
                                </tr>
                                {section.rows.map((row) => (
                                    <tr
                                        key={row.label}
                                        className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]"
                                    >
                                        <td className="px-4 py-3 text-sm text-[#525252] sticky left-0 bg-white hover:bg-[#FAFAFA]">
                                            {row.label}
                                        </td>
                                        {ROLES.map((r) => (
                                            <td
                                                key={r.id}
                                                className="px-3 py-3 text-center"
                                                data-testid={`rpm-cell-${r.id}-${row.label.replace(/\s+/g, "-").slice(0, 30)}`}
                                            >
                                                {row.perms[r.id] ? (
                                                    <Check size={16} weight="bold" className="inline text-emerald-600" />
                                                ) : (
                                                    <XIcon size={14} weight="bold" className="inline text-[#D4D4D4]" />
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-[10px] text-[#A3A3A3] mt-4">
                Last synced with backend on Feb 8, 2026. When roles are adjusted server-side,
                this matrix must be updated too (see <code className="bg-[#FAFAFA] px-1">frontend/src/pages/settings/RolePermissionsMatrix.jsx</code>).
            </p>
        </div>
    );
}
